import {IUserService} from './user.service.interface';
import {action, observable, makeObservable} from 'mobx';
import {ServiceBase} from '../ServiceBase';
import {IServiceFactoryExtended} from '../service-factory-extended.interface';
import {UserData} from '../sdk/sdk.service.interface';
import {Check, TimeSpan} from '@sparkware/app-utils';
import {OnOauthStateChangedParams} from '../sdk/sdk-events';
import {GetPlatformTokenResponse} from "../sdk/cgp-sdk.interface";
import {IAutoLoginParams} from '../../../common/interfaces/autologin-params.interface';
import {MarketingParams} from "../../../common/interfaces/marketing-params.interface";
import MIGRATION from "../../../common/const/migration_consts";
import {GNWP_SOURCES} from "../gnwp/gnwp-consts";
import {NullableString} from '../../../common/types/nullable-types';
import {GamesOpenSessionsKind} from "./games-open-sessions-kind.enum";

const AUTOLOGIN_QUERY_PARAM = 'UserInfo';

export class UserService extends ServiceBase implements IUserService {


    constructor(services: IServiceFactoryExtended) {
        super(services);

        makeObservable(this);

        this.waitForAutoLogin();
        this._updateUserData({});
        this.services.sdk.events.onOAuthStateChanged(this._updateUserData);
    }

    @observable.ref
    isAuthorized: boolean = false;
    @observable.ref
    logoutReason: number = null;


    @observable.ref
    private _userData: UserData = {};

    @action.bound
    private _updateUserData(params: OnOauthStateChangedParams) {

        params = params || {};

        this.isAuthorized = this.services.sdk.isAuthorized;

        if (Check.isNullOrUndefined(params.logoutReason)) {
            this.logoutReason = null;
        } else {
            this.logoutReason = params.logoutReason;
        }

        const userData = (this.services.sdk.userData || {}) as UserData;

        if (!this.isAuthorized) {
            const playerStatus = this.services.localStorage.getItem('playerStatus');
            this.services.window.scrollTo(0, 0);
            this._userData = {...userData, statusGroupId: (playerStatus ? parseInt(playerStatus) : undefined)};
        } else {
            this._userData = userData;
            this.services.localStorage.setItem('playerStatus', userData.statusGroupId);

            this._checkForFaultyCStatus();
        }

        this._updateSegments();
    }

    // REQUIREMENT 1627624 - check if cStatus if false but user is logged in or FTD
    private _checkForFaultyCStatus() {
        const cStatus = this.services.cmsSegmentation.isCstatus;

        //cStatus is CMS segmentation indicates if the user is registered or FDL
        if (this.isAuthorized && cStatus === false) {
            //this.services.analytics.sendAnalytics("ERROR", "LOGGED_IN_CSTATUS_FALSE", this.userData.cid);
            console.log('_checkForFaultyCStatus: ' + cStatus);
        }
    }

    @action.bound
    private _updateSegments() {
        this.segments = {
            IsAuthorized: this.isAuthorized,
            statusGroupId: this._userData.statusGroupId || 0,
            IsVIP: this._userData.IsVIP || false,
            GenderId: this._userData.GenderId || null,
            DaysSinceRegistration: this.daysSinceRegistration,
            RegistrationCountry: this.registrationCountry?.toLowerCase()
        }
    }

    @observable.ref
    segments = {};


    get hasActiveChallenges(): boolean {
        return this.services.sdk.activeChallengeCount > 0;
    }


    get name(): string {
        return this._userData.userName || '';
    }


    get cid(): string {
        return this._userData.cid || '';
    }

    get registrationCountry(): string {
        return this._userData.registrationCountry;
    }

    get joinDate(): string {
        return this._userData.joinDate;
    }

    get lastLogin(): NullableString {
        try {
            if (Check.isNullOrUndefined(this._userData.lastLogin) &&
                Check.isNullOrUndefined(this._userData.lastLoginDateInTimeZone)) {
                return null
            } else {                
                return this.services.time.getTimeFormatToRegulationService(this._userData.lastLogin, this._userData.lastLoginDateInTimeZone);
            }
        } catch (err) {
            this.services.logger.error('Failed to parse last login', err);
            return null;
        }

    }

    get userData() {
        return this._userData;
    }

    get isVip(): boolean {
        return (this._userData.statusGroupId > 0 && this._userData.statusGroupId !== 4);
    }

    get timeSinceRegistration(): TimeSpan {
        if (!this._userData)
            return null;
        if (!this._userData.timeSinceRegistration)
            return null;
        return TimeSpan.parse(this._userData.timeSinceRegistration);
    }

    get daysSinceRegistration(): number {
        if (this.timeSinceRegistration)
            return this.timeSinceRegistration.totalDays;

        return Infinity;
    }

    get playerRMSStatus(): number {
        return this._userData.playerRMStatus;
    }

    get countryName(): string {
        return this.services.sdk.getCountryName();
    }

    getAutoLoginToken(): Promise<string> {
        return this.services.sdk.getAutoLoginToken().then(data => data.token);
    }

    getPlatformToken(): Promise<GetPlatformTokenResponse> {
        return this.services.sdk.getPlatformToken();
    }

    getFirebaseToken(): string {
        return this.services.sdk.getFirebaseToken();
    }

    async openLogin(marketingParams?: MarketingParams): Promise<void> {
        await this.services.sdk.openLoginForm(marketingParams);
    }

    isDocUploadRequired(): boolean {
        return this.services.sdk.getAccountData()?.IsDocUploadRequired;
    }

    logout(): Promise<void> {
        return this.services.sdk.logout();
    }

    get genderTitle(): string {
        switch (this._userData.GenderId) {
            case 1:
                return 'MR_TITLE';
            case 2:
                return 'MRS_TITLE';
            default:
                return '';
        }
    }


    private _isInAutoLogin(): boolean {
        return this.services.sdk.isInAutologin();
    }

    waitForAutoLogin(): Promise<void> {

        if (!this._isInAutoLogin()) {
            return Promise.resolve();
        }


        return this.services.sdk.waitForAutologin()
            .then(() => {
                return this._removeAutoLoginTokenFromUrl()
                    .catch((err) => {
                        this.services.logger.error('Remove auto login token from URL failed!', err);
                    });

            });

    }

    async ensureUserIsLoggedIn(initiator: string): Promise<boolean> {
        await this.waitForAutoLogin();

        if (!this.isAuthorized) {
            try {
                await this.openLogin();
            } catch (err) {
                this.services.logger.debug(initiator + " - login cancelled or failed.", err);
            }
        }
        return this.isAuthorized;
    }


    private _removeAutoLoginTokenFromUrl(): Promise<void> {
        let paramToBeRemoved: string = AUTOLOGIN_QUERY_PARAM;
        const currentUri = this.services.location.currentUri;
        if (currentUri.queryParams.hasParam(MIGRATION.MIGRATION_TOKEN_QUERY_PARAM)
            && currentUri.queryParams.getParamValue(MIGRATION.MIGRATION_SOURCE_QUERY_PARAM)?.toLowerCase() === MIGRATION.MIGRATION_SOURCE_QUERY_PARAM_VALUE) {
            paramToBeRemoved = MIGRATION.MIGRATION_TOKEN_QUERY_PARAM;
        }
        const urlWithoutToken = currentUri.removeSearchParam(paramToBeRemoved).toString();
        return this.services.location.replace(urlWithoutToken);
    }


    runAutoLogin(autoLoginParams: IAutoLoginParams): Promise<void> {
        return this.services.sdk.runAutoLogin(autoLoginParams);
    }

    panicRestrictUser(): Promise<void> {
        const offset = this.services.time.getTimezoneOffset();

        return this.services.api.userController.selfExclusionPanic({
            timezoneOffset: offset,
            productPackage: this.services.sdk.getProductPackageId()
        }).then(() => {
            this.logout();
        }).catch((err) => {
            this.services.sdk.structuredLoggingService
                .getLogger()
                .addCategory("PANIC SELF EXCLUSION")
                .addAction("SelfExclusion fails")
                .addValue(JSON.stringify(err))
                .error();
            this.logout();
            this.services.logger.error('Self Exclusion Panic Failed.', err);
        });
    }

    get loginIP(): string | null {
        return this.services.sdk.tokenInfo?.loginIP;
    }

    async getOpenSessionsRealMoneyBalance(): Promise<number> {
        if (!this.isAuthorized) {
            return 0;
        }


        const openSessions = await this.services.api.openSessionsController.getOpenSessions({
            openSessionsKind: GamesOpenSessionsKind.Real
        });

        return openSessions.sessionDetails.sum(s => s.Credit);

    }

    getTempAccountGnwpSource(): GNWP_SOURCES {
        return this.services.gnwp.getTempAccountGnwpSource();
    }

}
